import { useEffect, useRef, useState } from 'react';
import { fetchAssignments } from '../api';
import { SENIORITY_LABELS, WORK_MODE_LABELS, formatDate } from '../lib/format';
import type { AssignmentListItem, Filters, Meta } from '../types';

export interface DrawerTarget {
  /** Tekniken som klickades. Utelämnas när hela området ska visas. */
  tech?: string;
  areaId?: string;
  areaName?: string;
}

interface AssignmentDrawerProps {
  target: DrawerTarget | null;
  /** Vyns aktiva filter – panelen visar samma urval, inte hela databasen. */
  filters: Filters;
  meta: Meta | undefined;
  onClose: () => void;
  /** Tar med sig urvalet till Uppdrag-fliken. */
  onShowAll?: (filters: Partial<Filters>) => void;
}

const PAGE_SIZE = 50;

/**
 * Panel som listar de annonser bakom en siffra. Poängen är att komma från
 * "Integration nämns i 28 uppdrag" till de 28 länkarna utan att tappa
 * sammanhanget – därför en sidopanel i stället för ett filterbyte.
 */
export function AssignmentDrawer({ target, filters, meta, onClose, onShowAll }: AssignmentDrawerProps) {
  const [items, setItems] = useState<AssignmentListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const isOpen = target !== null;

  useEffect(() => {
    if (!isOpen) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = originalOverflow;
      previouslyFocused.current?.focus();
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!target) return;

    const controller = new AbortController();
    setLoading(true);
    setError(undefined);

    fetchAssignments(drawerFilters(filters, target), 1, PAGE_SIZE, controller.signal)
      .then((page) => {
        setItems(page.items);
        setTotal(page.total);
      })
      .catch((cause: Error) => {
        if (cause.name !== 'AbortError') setError(cause.message);
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [target, filters]);

  if (!target) return null;

  const sourceName = (id: string) => meta?.sources.find((s) => s.id === id)?.name ?? id;
  const heading = target.tech ?? target.areaName ?? 'Uppdrag';
  const subheading = target.tech && target.areaName ? `inom ${target.areaName}` : 'i urvalet';

  return (
    <>
      <div className="drawer__backdrop" onClick={onClose} aria-hidden="true" />

      <aside className="drawer" role="dialog" aria-modal="true" aria-label={`Uppdrag för ${heading}`}>
        <header className="drawer__header">
          <div>
            <p className="drawer__eyebrow">{subheading}</p>
            <h2 className="drawer__title">{heading}</h2>
            <p className="drawer__count">
              {loading && items.length === 0 ? 'Hämtar…' : `${total} uppdrag`}
              {total > PAGE_SIZE ? ` · visar de ${PAGE_SIZE} senaste` : ''}
            </p>
          </div>
          <button ref={closeButtonRef} type="button" className="icon-button" onClick={onClose} title="Stäng (Esc)">
            <span aria-hidden="true">×</span>
            <span className="visually-hidden">Stäng</span>
          </button>
        </header>

        <div className="drawer__body">
          {error ? (
            <p className="empty">Kunde inte hämta uppdragen: {error}</p>
          ) : loading && items.length === 0 ? (
            <div style={{ display: 'grid', gap: 10 }}>
              {Array.from({ length: 5 }, (_, i) => (
                <div key={i} className="skeleton" style={{ height: 92 }} />
              ))}
            </div>
          ) : items.length === 0 ? (
            <p className="empty">Inga uppdrag matchar.</p>
          ) : (
            <ol className="drawer__list">
              {items.map((item) => (
                <li key={item.id} className="assignment-card">
                  <a
                    className="assignment-card__title"
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {item.title}
                    <span className="assignment-card__external" aria-hidden="true">
                      ↗
                    </span>
                  </a>

                  <p className="assignment-card__meta">
                    {[
                      formatDate(item.publishedAt ?? item.firstSeen) + (item.dateEstimated ? ' *' : ''),
                      sourceName(item.source),
                      item.location,
                      item.client,
                      item.seniority ? SENIORITY_LABELS[item.seniority] : undefined,
                      item.workMode && item.workMode !== 'unknown' ? WORK_MODE_LABELS[item.workMode] : undefined,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>

                  {item.excerpt ? <p className="assignment-card__excerpt">{item.excerpt}</p> : null}

                  {item.technologies.length > 0 ? (
                    <div className="assignment-card__tags">
                      {item.technologies.slice(0, 8).map((tech) => (
                        <span key={tech} className={`tag${tech === target.tech ? ' tag--match' : ''}`}>
                          {tech}
                        </span>
                      ))}
                      {item.technologies.length > 8 ? (
                        <span className="tag">+{item.technologies.length - 8}</span>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </div>

        {onShowAll && items.length > 0 ? (
          <footer className="drawer__footer">
            <button
              type="button"
              className="chip chip--active"
              onClick={() => {
                onShowAll({
                  ...(target.areaId ? { area: target.areaId as Filters['area'] } : {}),
                  tech: target.tech ? [...new Set([...filters.tech, target.tech])] : filters.tech,
                });
                onClose();
              }}
            >
              Öppna i Uppdrag-fliken
            </button>
            <span className="panel__tools">Där kan du sortera, bläddra och filtrera vidare.</span>
          </footer>
        ) : null}
      </aside>
    </>
  );
}

function drawerFilters(filters: Filters, target: DrawerTarget): Filters {
  return {
    ...filters,
    ...(target.areaId ? { area: target.areaId as Filters['area'] } : {}),
    tech: target.tech ? [...new Set([...filters.tech, target.tech])] : filters.tech,
  };
}
